<script setup lang="ts">
import NodeReprInput, {
    type ValueSlotProps,
    type TermSlotProps,
    type PredicateValueInputMethod,
    type PredicateValueDisplayFormat,
} from './NodeReprInput.vue';
import Button from 'primevue/button';
import {
    NodeRepr,
    NodeKind,
    createEmptyReprWithKind,
} from '../helpers/internal-repr';
import { computed, type VNode } from 'vue';
import NodeReprDropZone from './NodeReprDropZone.vue';
import type { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';

const props = defineProps<{
    parentPath: number[];
    readonly?: boolean;
    valueLabelFormat: PredicateValueDisplayFormat;
    valueInputMethod: PredicateValueInputMethod;
    renderValue: ((value: ValueSlotProps) => VNode[]) | null | undefined;
    renderTerm: ((value: TermSlotProps) => VNode[]) | null | undefined;
    modelValue: NodeRepr[];
    parentInputId: string;
    parentInputName: string;
    parentKind: NodeKind;
    allowEquiv?: boolean;
    allowNonRootValue?: boolean;
    allowUndecidableValue?: boolean;
    symbolLabels?: boolean;
    symbolValueLabels?: boolean;
    testTermBindings: Record<string, PredicateValue> | null;
    testTautologies?: boolean;
    testContradictions?: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: NodeRepr[]): void;
    (
        e: 'update:testTermBindings',
        value: Record<string, PredicateValue> | null
    ): void;
}>();

const items = computed<NodeRepr[]>({
    get: () => props.modelValue,
    set: (newValue: NodeRepr[]) => {
        emit('update:modelValue', newValue);
    },
});

function deleteItem(index: number) {
    items.value = items.value.toSpliced(index, 1);
}

function addItem(
    index?: number | null | undefined,
    newItem?: NodeRepr | NodeKind | null | undefined
): void {
    if (typeof newItem === 'string' || !newItem) {
        const newKind =
            (newItem as NodeKind | null | undefined) ?? NodeKind.Term;
        newItem = createEmptyReprWithKind(newKind);
    }

    if (typeof index === 'number') {
        items.value = items.value.toSpliced(index, 0, newItem);
    } else {
        const newItems = [...items.value];
        newItems.push(newItem as NodeRepr);
        items.value = newItems;
    }
}

function updateItem(index: number, newValue: NodeRepr) {
    items.value = items.value.toSpliced(index, 1, newValue);
}
</script>

<template>
    <div class="expr-node-input-list">
        <template v-for="(item, ix) of items" :key="ix">
            <div class="expr-node-input-list-item">
                <Button
                    v-if="!readonly"
                    class="remove-item"
                    icon="pi pi-minus"
                    text
                    severity="danger"
                    @click="deleteItem(ix)"
                />

                <NodeReprInput
                    class="node-item-input"
                    :model-value="item"
                    :value-label-format="valueLabelFormat"
                    :value-input-method="valueInputMethod"
                    :path="[...parentPath, ix]"
                    :readonly="readonly"
                    draggable
                    :render-term="renderTerm"
                    :render-value="renderValue"
                    :parent-kind="parentKind"
                    :input-id="`${parentInputId}-${ix}`"
                    :input-name="`${parentInputName}-${ix}`"
                    :allow-equiv="allowEquiv"
                    :allow-non-root-value="allowNonRootValue"
                    :allow-undecidable-value="allowUndecidableValue"
                    :symbol-labels="symbolLabels"
                    :symbol-value-labels="symbolValueLabels"
                    :test-term-bindings="testTermBindings"
                    :test-tautologies="testTautologies"
                    :test-contradictions="testContradictions"
                    @update:model-value="(newValue) => updateItem(ix, newValue)"
                    @update:test-term-bindings="
                        emit('update:testTermBindings', $event)
                    "
                />
            </div>
        </template>
        <div v-if="!readonly" class="expr-node-input-list-next-item">
            <Button
                icon="pi pi-plus"
                text
                severity="success"
                @click="addItem()"
            />
            <NodeReprDropZone
                severity="success"
                style="width: 100%"
                :path="[...parentPath, items.length]"
            ></NodeReprDropZone>
        </div>
    </div>
</template>

<style scoped lang="scss">
.expr-node-input-list {
    .expr-node-input-list-next-item {
        display: flex;
    }

    .expr-node-input-list-item {
        display: flex;
        .remove-item {
            flex-grow: 0;
            flex-shrink: 0;
        }
        .node-item-input {
            flex-grow: 1;
            flex-shrink: 1;
            width: 100%;
        }
    }
}
</style>
